import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import CloseIcon from "@mui/icons-material/Close";
import { baseurl } from "../../config/url";

const formatDateIN = (date) => {
  if (!date) return "N/A";
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return "N/A";
  return d.toLocaleDateString("en-IN");
};

const AdminSellers = () => {
  const [sellers, setSellers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  // Edit seller modal
  const [editSeller, setEditSeller] = useState(null);

  // Agreement modal
  const [agreementSellerId, setAgreementSellerId] = useState(null);
  const [agreementInfo, setAgreementInfo] = useState({
    registrationDate: "",
    agreementDate: "",
    expiryDate: "",
  });

  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState("");

  // ✅ Pagination
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const token = localStorage.getItem("token") || localStorage.getItem("adminToken") || "";
  const axiosConfig = useMemo(
    () => ({
      headers: { Authorization: `Bearer ${token}` },
    }),
    [token]
  );

  const filteredSellers = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return sellers;
    return sellers.filter((s) => {
      const name = (s?.name || "").toLowerCase();
      const id = (s?._id || "").toLowerCase();
      const email = (s?.email || "").toLowerCase();
      const mobile = String(s?.mobile || "").toLowerCase();
      const company = (s?.companyName || "").toLowerCase();
      return (
        name.includes(q) ||
        id.includes(q) ||
        email.includes(q) ||
        mobile.includes(q) ||
        company.includes(q)
      );
    });
  }, [sellers, searchTerm]);

  // ✅ Reset to page 1 when filtering changes
  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredSellers.length / pageSize));

  // ✅ Clamp page if list shrinks after delete/filter
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const pagedSellers = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredSellers.slice(start, start + pageSize);
  }, [filteredSellers, page]);

  const fetchSellers = async () => {
    setError("");
    setLoading(true);
    try {
      const res = await axios.get(`${baseurl}/api/seller/all-seller`, axiosConfig);
      const data = Array.isArray(res.data) ? res.data : res.data?.sellers || [];
      setSellers(data);
    } catch (e) {
      console.error("Error fetching sellers:", e?.response?.data || e?.message);
      setError("Failed to load sellers. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSellers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBlockToggle = async (id, isBlocked) => {
    if (!id) return;
    setActionLoading(true);
    try {
      const endpoint = isBlocked
        ? `${baseurl}/api/admin/unblock-seller/${id}`
        : `${baseurl}/api/admin/block-seller/${id}`;

      await axios.put(endpoint, {}, axiosConfig);

      setSellers((prev) =>
        prev.map((s) => (s._id === id ? { ...s, isBlocked: !isBlocked } : s))
      );
    } catch (e) {
      console.error("Error toggling block status:", e?.response?.data || e?.message);
      alert("Failed to update seller status.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!id) return;
    const ok = window.confirm("Are you sure you want to delete this seller?");
    if (!ok) return;

    setActionLoading(true);
    try {
      await axios.delete(`${baseurl}/api/admin/delete-seller/${id}`, axiosConfig);
      setSellers((prev) => prev.filter((s) => s._id !== id));
      alert("Seller deleted successfully!");
    } catch (e) {
      console.error("Error deleting seller:", e?.response?.data || e?.message);
      alert("Failed to delete seller.");
    } finally {
      setActionLoading(false);
    }
  };

  // --- Edit seller ---
  const openEdit = (seller) => {
    setEditSeller({
      _id: seller?._id,
      name: seller?.name || "",
      email: seller?.email || "",
      mobile: seller?.mobile || "",
      password: "",
    });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editSeller?._id) return;

    setActionLoading(true);
    try {
      const updateData = {
        name: editSeller.name,
        email: editSeller.email,
        mobile: editSeller.mobile,
      };
      if (editSeller.password?.trim()) updateData.password = editSeller.password.trim();

      await axios.put(
        `${baseurl}/api/admin/update-seller/${editSeller._id}`,
        updateData,
        axiosConfig
      );

      setSellers((prev) =>
        prev.map((s) =>
          s._id === editSeller._id
            ? { ...s, name: updateData.name, email: updateData.email, mobile: updateData.mobile }
            : s
        )
      );

      setEditSeller(null);
      alert("Seller updated successfully!");
    } catch (e2) {
      console.error("Error updating seller:", e2?.response?.data || e2?.message);
      alert("Failed to update seller.");
    } finally {
      setActionLoading(false);
    }
  };

  // --- Agreement ---
  const openAgreement = async (seller) => {
    const sellerId = seller?._id;
    if (!sellerId) return;

    setActionLoading(true);
    try {
      const res = await axios.get(`${baseurl}/api/seller/agreement/${sellerId}`, axiosConfig);

      setAgreementSellerId(sellerId);
      setAgreementInfo({
        registrationDate: res.data?.registrationDate || seller?.createdAt || "",
        agreementDate: res.data?.agreementDate || "",
        expiryDate: res.data?.expiryDate || "",
      });
    } catch (e) {
      console.error("❌ Error fetching agreement:", e?.response?.data || e?.message);
      alert("Error fetching agreement. Check console for details.");
    } finally {
      setActionLoading(false);
    }
  };

  const saveAgreement = async () => {
    if (!agreementSellerId) return;

    setActionLoading(true);
    try {
      await axios.post(
        `${baseurl}/api/seller/agreement/${agreementSellerId}`,
        { agreementDate: agreementInfo.agreementDate, expiryDate: agreementInfo.expiryDate },
        axiosConfig
      );

      alert("Agreement updated successfully!");

      const res = await axios.get(`${baseurl}/api/seller/agreement/${agreementSellerId}`, axiosConfig);
      setAgreementInfo((prev) => ({
        ...prev,
        agreementDate: res.data?.agreementDate || prev.agreementDate,
        expiryDate: res.data?.expiryDate || prev.expiryDate,
        registrationDate: res.data?.registrationDate || prev.registrationDate,
      }));
    } catch (e) {
      console.error("❌ Error saving agreement:", e?.response?.data || e?.message);
      alert("Error updating agreement. Check console for details.");
    } finally {
      setActionLoading(false);
    }
  };

  const startIndex = (page - 1) * pageSize + 1;
  const endIndex = Math.min(page * pageSize, filteredSellers.length);

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Manage Sellers</h1>
            <p className="text-sm text-gray-600 mt-1">
              Search, block/unblock, edit seller info, and manage agreement dates.
            </p>
          </div>

          <button
            onClick={fetchSellers}
            disabled={loading || actionLoading}
            className="px-4 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 transition disabled:opacity-60"
          >
            Refresh
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-lg border border-red-200 bg-red-50 text-red-700 text-sm">
            {error}
          </div>
        )}

        {/* Search */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm mb-4">
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
            <div className="flex-1">
              <label className="text-sm font-semibold text-gray-700">Search</label>
              <input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by ID, name, email, mobile, company..."
                className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>

            <div className="flex gap-3">
              <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm">
                <div className="text-gray-500">Total</div>
                <div className="font-bold text-gray-900">{sellers.length}</div>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm">
                <div className="text-gray-500">Filtered</div>
                <div className="font-bold text-gray-900">{filteredSellers.length}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-x-auto">
          {loading ? (
            <div className="p-6 text-gray-600">Loading sellers...</div>
          ) : filteredSellers.length === 0 ? (
            <div className="p-6 text-gray-600">No sellers found.</div>
          ) : (
            <>
              <table className="w-full text-sm">
                <thead className="bg-gray-100 text-gray-700">
                  <tr>
                    <th className="px-5 py-3 text-left font-semibold">Seller</th>
                    <th className="px-5 py-3 text-left font-semibold">Contact</th>
                    <th className="px-5 py-3 text-left font-semibold">Company</th>
                    <th className="px-5 py-3 text-left font-semibold">Status</th>
                    <th className="px-5 py-3 text-left font-semibold">Created</th>
                    <th className="px-5 py-3 text-right font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedSellers.map((s) => (
                    <tr key={s._id} className="border-t hover:bg-gray-50">
                      <td className="px-5 py-4">
                        <div className="font-semibold text-gray-900">{s.name || "N/A"}</div>
                        <div className="text-xs text-gray-500 mt-1 break-all">{s._id}</div>
                      </td>

                      <td className="px-5 py-4 text-gray-700">
                        <div className="break-all">{s.email || "N/A"}</div>
                        <div className="text-xs text-gray-500 mt-1">{s.mobile || "N/A"}</div>
                      </td>

                      <td className="px-5 py-4 text-gray-700">
                        <div className="font-medium">{s.companyName || "N/A"}</div>
                        <div className="text-xs text-gray-500 mt-1">GST: {s.gstNumber || "N/A"}</div>
                      </td>

                      <td className="px-5 py-4">
                        <span
                          className={[
                            "inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold",
                            s.isBlocked ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700",
                          ].join(" ")}
                        >
                          {s.isBlocked ? "Blocked" : "Active"}
                        </span>
                      </td>

                      <td className="px-5 py-4 text-gray-700">{formatDateIN(s.createdAt)}</td>

                      <td className="px-5 py-4">
                        <div className="flex items-center justify-end gap-2 flex-wrap">
                          <button
                            onClick={() => openAgreement(s)}
                            disabled={actionLoading}
                            className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 transition disabled:opacity-60"
                          >
                            Agreement
                          </button>

                          <button
                            onClick={() => openEdit(s)}
                            disabled={actionLoading}
                            className="px-3 py-1.5 rounded-lg bg-yellow-500 text-white hover:bg-yellow-600 transition disabled:opacity-60"
                          >
                            Edit
                          </button>

                          <button
                            onClick={() => handleBlockToggle(s._id, s.isBlocked)}
                            disabled={actionLoading}
                            className="px-3 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition disabled:opacity-60"
                          >
                            {s.isBlocked ? "Unblock" : "Block"}
                          </button>

                          <button
                            onClick={() => handleDelete(s._id)}
                            disabled={actionLoading}
                            className="px-3 py-1.5 rounded-lg bg-red-600 text-white hover:bg-red-700 transition disabled:opacity-60"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* ✅ Pagination Footer */}
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 p-4 border-t border-gray-200">
                <div className="text-sm text-gray-600">
                  Showing <span className="font-semibold">{startIndex}</span>-
                  <span className="font-semibold">{endIndex}</span> of{" "}
                  <span className="font-semibold">{filteredSellers.length}</span>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage(1)}
                    disabled={page === 1}
                    className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    First
                  </button>
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Prev
                  </button>

                  <div className="px-3 py-1.5 rounded-lg bg-gray-100 border border-gray-200 text-sm">
                    Page <span className="font-semibold">{page}</span> /{" "}
                    <span className="font-semibold">{totalPages}</span>
                  </div>

                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                  <button
                    onClick={() => setPage(totalPages)}
                    disabled={page === totalPages}
                    className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Last
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Agreement Modal */}
        {agreementSellerId && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="w-full max-w-md bg-white rounded-xl shadow-xl border border-gray-200 relative">
              <button
                className="absolute top-3 right-3 text-gray-600 hover:text-gray-900"
                onClick={() => setAgreementSellerId(null)}
                aria-label="Close"
              >
                <CloseIcon />
              </button>

              <div className="p-5 border-b border-gray-200">
                <h2 className="text-lg font-bold text-gray-900">Agreement Dates</h2>
                <p className="text-xs text-gray-500 mt-1">Dates are shown in DD/MM/YYYY</p>
              </div>

              <div className="p-5 space-y-4">
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Registration</span>
                    <span className="font-semibold text-gray-900">
                      {formatDateIN(agreementInfo.registrationDate)}
                    </span>
                  </div>
                  <div className="flex justify-between mt-1">
                    <span className="text-gray-600">Agreement</span>
                    <span className="font-semibold text-gray-900">
                      {formatDateIN(agreementInfo.agreementDate)}
                    </span>
                  </div>
                  <div className="flex justify-between mt-1">
                    <span className="text-gray-600">Expiry</span>
                    <span className="font-semibold text-gray-900">
                      {formatDateIN(agreementInfo.expiryDate)}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700">Agreement Date</label>
                  <input
                    type="date"
                    value={agreementInfo.agreementDate || ""}
                    onChange={(e) =>
                      setAgreementInfo((p) => ({ ...p, agreementDate: e.target.value }))
                    }
                    className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700">Expiry Date</label>
                  <input
                    type="date"
                    value={agreementInfo.expiryDate || ""}
                    onChange={(e) =>
                      setAgreementInfo((p) => ({ ...p, expiryDate: e.target.value }))
                    }
                    className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg"
                  />
                </div>

                <button
                  onClick={saveAgreement}
                  disabled={actionLoading}
                  className="w-full px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 transition disabled:opacity-60"
                >
                  {actionLoading ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {editSeller && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="w-full max-w-md bg-white rounded-xl shadow-xl border border-gray-200 relative">
              <button
                className="absolute top-3 right-3 text-gray-600 hover:text-gray-900"
                onClick={() => setEditSeller(null)}
                aria-label="Close"
              >
                <CloseIcon />
              </button>

              <div className="p-5 border-b border-gray-200">
                <h2 className="text-lg font-bold text-gray-900">Edit Seller</h2>
                <p className="text-xs text-gray-500 mt-1">Update basic seller details.</p>
              </div>

              <form onSubmit={saveEdit} className="p-5 space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700">Name</label>
                  <input
                    type="text"
                    value={editSeller.name}
                    onChange={(e) => setEditSeller((p) => ({ ...p, name: e.target.value }))}
                    className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700">Email</label>
                  <input
                    type="email"
                    value={editSeller.email}
                    onChange={(e) => setEditSeller((p) => ({ ...p, email: e.target.value }))}
                    className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700">Mobile</label>
                  <input
                    type="text"
                    value={editSeller.mobile}
                    onChange={(e) => setEditSeller((p) => ({ ...p, mobile: e.target.value }))}
                    className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700">
                    Password <span className="text-xs text-gray-500">(optional)</span>
                  </label>
                  <input
                    type="password"
                    value={editSeller.password || ""}
                    onChange={(e) => setEditSeller((p) => ({ ...p, password: e.target.value }))}
                    className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg"
                  />
                </div>

                <div className="flex gap-2">
                  <button
                    type="submit"
                    disabled={actionLoading}
                    className="flex-1 px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition disabled:opacity-60"
                  >
                    {actionLoading ? "Saving..." : "Save"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditSeller(null)}
                    className="flex-1 px-4 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 transition"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default AdminSellers;

























// import React, { useState, useEffect } from "react";
// import axios from "axios";
// import CloseIcon from "@mui/icons-material/Close";
// import { baseurl } from "../../config/url";

// const AdminSellers = () => {
//   const [sellers, setSellers] = useState([]);
//   const [filteredSellers, setFilteredSellers] = useState([]);
//   const [searchTerm, setSearchTerm] = useState("");
//   const [selectedSeller, setSelectedSeller] = useState(null); // For editing a seller
//   const [selectedSellerId, setSelectedSellerId] = useState(null); 
//   const [error, setError] = useState("");
//   const [agreementDate, setAgreementDate] = useState("");
//   const [expiryDate, setExpiryDate] = useState("");

//   const token = localStorage.getItem("token"); // Retrieve token from localStorage

//   const config = {
//     headers: {
//       Authorization: `Bearer ${token}`, // Attach token as Bearer
//     },
//   };

//   useEffect(() => {
//     const fetchSellers = async () => {
//       try {
//         const response = await axios.get(`${baseurl}/api/seller/all-seller`, config);
//         setSellers(response.data);
//         setFilteredSellers(response.data); // Initialize filtered sellers with all sellers
//       } catch (error) {
//         console.error("Error fetching sellers:", error.message);
//         setError("Failed to load sellers. Please try again.");
//       }
//     };

//     fetchSellers();
//   }, []);

//   const handleSearch = (e) => {
//     const search = e.target.value.toLowerCase();
//     setSearchTerm(search);

//     const filtered = sellers.filter(
//       (seller) =>
//         seller.name.toLowerCase().includes(search) || seller._id.toLowerCase().includes(search)
//     );
//     setFilteredSellers(filtered);
//   };

//   const handleBlockToggle = async (id, isBlocked) => {
//     try {
//       const endpoint = isBlocked
//         ? `${baseurl}/api/admin/unblock-seller/${id}`
//         : `${baseurl}/api/admin/block-seller/${id}`;
//       await axios.put(endpoint, {}, config);
//       setSellers(
//         sellers.map((seller) =>
//           seller._id === id ? { ...seller, isBlocked: !isBlocked } : seller
//         )
//       );
//       setFilteredSellers(
//         filteredSellers.map((seller) =>
//           seller._id === id ? { ...seller, isBlocked: !isBlocked } : seller
//         )
//       );
//       alert(`Seller ${isBlocked ? "unblocked" : "blocked"} successfully!`);
//     } catch (error) {
//       console.error("Error toggling block status:", error.message);
//     }
//   };

//   const handleDelete = async (id) => {
//     try {
//       await axios.delete(`${baseurl}/api/admin/delete-seller/${id}`, config);
//       const updatedSellers = sellers.filter((seller) => seller._id !== id);
//       setSellers(updatedSellers);
//       setFilteredSellers(updatedSellers);
//       alert("Seller deleted successfully!");
//     } catch (error) {
//       console.error("Error deleting seller:", error.message);
//     }
//   };

//   const handleEditSeller = (seller) => {
//     setSelectedSeller(seller); // Set the seller for editing
//   };


//   const handleUpdateSeller = async (e) => {
//     e.preventDefault();
//     try {
//         const { _id, name, email, mobile, password } = selectedSeller;
//         const updateData = { name, email, mobile };
//         if (password) updateData.password = password; // Include password only if provided
        
//         await axios.put(`${baseurl}/api/admin/update-seller/${_id}`, updateData, config);
      
//     } catch (error) {
//         console.error("Error updating seller:", error.message);
//     }
// };

// const handleViewAgreement = async (sellerId) => {
//   try {
//     const response = await axios.get(`${baseurl}/api/seller/agreement/${sellerId}`, config);

//     setAgreementDate(response.data.agreementDate || "");
//     setExpiryDate(response.data.expiryDate || "");

//     setSelectedSeller({
//       ...selectedSeller,
//       createdAt: response.data.registrationDate || "Not Available" // Ensure createdAt is set
//     });

//     setSelectedSellerId(sellerId);
//   } catch (error) {
//     console.error("❌ Error fetching agreement:", error.response?.data || error.message);
//     alert("Error fetching agreement. Please check the console for details.");
//   }
// };



// const handleSaveAgreement = async () => {
//   try {
//     if (!selectedSellerId) {
//       alert("Please select a seller first.");
//       return;
//     }

//     console.log(`🔄 Updating agreement for Seller ID: ${selectedSellerId}`);

//     const response = await axios.post(
//       `${baseurl}/api/seller/agreement/${selectedSellerId}`, // ✅ Ensure correct API URL
//       { agreementDate, expiryDate },
//       config
//     );

//     console.log("✅ Agreement Updated Successfully:", response.data);

//     alert("Agreement date updated successfully!");

//     // Refresh agreement details after update
//     handleViewAgreement(selectedSellerId);
//   } catch (error) {
//     console.error("❌ Error saving agreement:", error.response?.data || error.message);
//     alert("Error updating agreement. Check console for details.");
//   }
// };


// // **Format Date Function**
// const formatDate = (date) => {
//   const d = new Date(date);
//   const day = d.getDate().toString().padStart(2, "0");  // Adds leading zero if the day is a single digit
//   const month = (d.getMonth() + 1).toString().padStart(2, "0");  // Adds leading zero if the month is a single digit
//   const year = d.getFullYear();
//   return `${day}/${month}/${year}`;  // Returns the date in dd/mm/yyyy format
// };



//   return (
//     <div className="min-h-screen bg-gradient-to-r from-gray-50 to-blue-50 py-10 px-4 lg:px-16">
//       <div className="relative flex flex-col">
//         <div className="flex-1 p-6 bg-white rounded-lg shadow-lg">
//           <h1 className="text-4xl font-extrabold text-center text-gray-800 mb-8">Manage Sellers</h1>

//           {error && <p className="text-red-500 text-center mb-4">{error}</p>}

//           {/* Search Bar */}
//           <div className="mb-6">
//             <input
//               type="text"
//               placeholder="Search by ID or Name"
//               value={searchTerm}
//               onChange={handleSearch}
//               className="w-full p-3 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
//             />
//           </div>

//           {/* Table Section */}
//           <div className="overflow-x-auto bg-white rounded-lg shadow-md">
//             {filteredSellers.length > 0 ? (
//               <table className="table-auto w-full text-sm border-collapse">
//                 <thead>
//                   <tr className="bg-gradient-to-r from-blue-400 to-blue-500 text-white">
//                     <th className="px-6 py-4 text-left font-bold">ID</th>
//                     <th className="px-6 py-4 text-left font-bold">Name</th>
//                     <th className="px-6 py-4 text-left font-bold">Email</th>
//                     <th className="px-6 py-4 text-left font-bold">Mobile</th>
//                     <th className="px-6 py-4 text-left font-bold">Status</th>
//                     <th className="px-6 py-4 text-center font-bold">Actions</th>
//                     <th className="px-6 py-4 text-center font-bold">Agreement Dates</th>
//                   </tr>
//                 </thead>
//                 <tbody>
//                   {filteredSellers.map((seller, index) => (
//                     <tr
//                       key={seller._id}
//                       className={`${
//                         index % 2 === 0 ? "bg-gray-50" : "bg-gray-100"
//                       } hover:bg-gray-200 transition-colors duration-300`}
//                     >
//                       <td className="px-6 py-4 text-gray-800 font-semibold">{seller._id}</td>
//                       <td className="px-6 py-4 text-gray-700">{seller.name}</td>
//                       <td className="px-6 py-4 text-gray-700">{seller.email}</td>
//                       <td className="px-6 py-4 text-gray-700">{seller.mobile}</td>
//                       <td className="px-6 py-4 text-gray-700">
//                         {seller.isBlocked ? "Blocked" : "Active"}
//                       </td>
//                       <td className="px-6 py-4 text-center">
//                         <button
//                           onClick={() => handleEditSeller(seller)}
//                           className="bg-yellow-500 text-white px-4 py-2 rounded-lg hover:bg-yellow-600 mr-2"
//                         >
//                           Edit
//                         </button>
//                         <button
//                           onClick={() => handleBlockToggle(seller._id, seller.isBlocked)}
//                           className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 mr-2"
//                         >
//                           {seller.isBlocked ? "Unblock" : "Block"}
//                         </button>
//                         <button
//                           onClick={() => handleDelete(seller._id)}
//                           className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600"
//                         >
//                           Delete
//                         </button>
//                       </td>
//                       <td className="px-6 py-4 text-center">
//                         <button
//                           onClick={() => handleViewAgreement(seller._id)}
//                           className="bg-purple-500 text-white px-4 py-2 rounded-lg hover:bg-purple-600"
//                         >
//                           View
//                         </button>
//                       </td>
//                     </tr>
//                   ))}
//                 </tbody>
//               </table>
//             ) : (
//               <p className="px-6 py-4 text-center text-gray-600 font-medium">
//                 No sellers found.
//               </p>
//             )}
//           </div>


//           {/* Agreement Date Modal */}




//                     {/* Agreement Date Modal */}
//                     {selectedSellerId && (
//             <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
//               <div className="bg-white p-6 rounded shadow-lg w-full max-w-md mx-4 relative">
//                 {/* Close Button */}
//                 <button 
//                   className="absolute top-2 right-2 text-gray-600 hover:text-gray-900"
//                   onClick={() => setSelectedSellerId(null)}
//                 >
//                   <CloseIcon fontSize="large" />
//                 </button>

//                 {/* Header */}
//                 <h2 className="text-xl font-bold mb-4 text-center">Agreement Date Details(DD/MM/YYYY)</h2>

//                 {/* Display Registration Date */}
//                 <p className="text-gray-700 text-center mb-4">
//                   <strong>Registration Date:</strong> 
//                   {selectedSeller?.createdAt 
//                     ? formatDate(selectedSeller.createdAt) 
//                     : "Not Available"}
//                 </p>

//                 {/* Display Saved Agreement & Expiry Dates */}
//                 <p className="text-gray-700 text-center mb-4">
//                   <strong>Agreement Date:</strong> 
//                   {agreementDate ? formatDate(agreementDate) : "Not Set"}
//                 </p>
//                 <p className="text-gray-700 text-center mb-4">
//                   <strong>Expiry Date:</strong> 
//                   {expiryDate ? formatDate(expiryDate) : "Not Set"}
//                 </p>

//                 {/* Editable Fields */}
//                 <label className="block text-gray-700 font-semibold">Agreement Date:</label>
//                 <input
//                   type="date"
//                   value={agreementDate}
//                   onChange={(e) => setAgreementDate(e.target.value)}
//                   className="w-full p-2 border rounded mb-4"
//                 />

//                 <label className="block text-gray-700 font-semibold">Expiry Date:</label>
//                 <input
//                   type="date"
//                   value={expiryDate}
//                   onChange={(e) => setExpiryDate(e.target.value)}
//                   className="w-full p-2 border rounded mb-4"
//                 />

//                 {/* Save Button */}
//                 <button 
//                   className="bg-green-500 text-white px-4 py-2 rounded w-full hover:bg-green-600"
//                   onClick={handleSaveAgreement}
//                 >
//                   Save
//                 </button>
//               </div>
//             </div>
//           )}
//           {/* Edit Seller Form */}
//           {selectedSeller && (
//             <div className="mt-6 p-4 bg-white shadow-lg rounded-lg">
//               <h2 className="text-xl font-bold mb-4">Edit Seller</h2>
//               <form onSubmit={handleUpdateSeller} className="space-y-4">
//                 <div>
//                   <label className="block text-gray-700">Name</label>
//                   <input
//                     type="text"
//                     value={selectedSeller.name}
//                     onChange={(e) =>
//                       setSelectedSeller({ ...selectedSeller, name: e.target.value })
//                     }
//                     className="w-full p-3 border rounded-lg"
//                   />
//                 </div>
//                 <div>
//                   <label className="block text-gray-700">Email</label>
//                   <input
//                     type="email"
//                     value={selectedSeller.email}
//                     onChange={(e) =>
//                       setSelectedSeller({ ...selectedSeller, email: e.target.value })
//                     }
//                     className="w-full p-3 border rounded-lg"
//                   />
//                 </div>
//                 <div>
//                   <label className="block text-gray-700">Mobile</label>
//                   <input
//                     type="text"
//                     value={selectedSeller.mobile}
//                     onChange={(e) =>
//                       setSelectedSeller({ ...selectedSeller, mobile: e.target.value })
//                     }
//                     className="w-full p-3 border rounded-lg"
//                   />
//                 </div>
//                 <div>
//     <label className="block text-gray-700">Password</label>
//     <input
//         type="password"
//         value={selectedSeller.password || ""}
//         onChange={(e) =>
//             setSelectedSeller({ ...selectedSeller, password: e.target.value })
//         }
//         className="w-full p-3 border rounded-lg"
//     />
// </div>

//                 <div className="flex space-x-4">
//                   <button
//                     type="submit"
//                     className="bg-green-500 text-white px-6 py-2 rounded-lg hover:bg-green-600"
//                   >
//                     Save
//                   </button>
//                   <button
//                     type="button"
//                     onClick={() => setSelectedSeller(null)}
//                     className="bg-gray-500 text-white px-6 py-2 rounded-lg hover:bg-gray-600"
//                   >
//                     Cancel
//                   </button>
//                 </div>
//               </form>
//             </div>
//           )}
//         </div>
//       </div>
//     </div>
//   );
// };

// export default AdminSellers;
